/**
 * The confirmation of an address (Asana 1218325456154102, option C chosen by
 * Maude on 28 October 2026): the email leaves at sign-up, nothing stops the
 * person from entering, a banner reminds them, and three actions wait for the
 * address: a referral code, the pro status, a paid activity.
 *
 * The API decides who is asked. Somebody is asked only once the email has
 * actually left, so the ~12 150 accounts of before, Google and Apple accounts,
 * and everybody while the SendGrid template is not pasted, are never asked and
 * see none of this. An API that does not know the route yet answers 404, and
 * the absence of an answer is read here as « not asked »: a banner shown on a
 * guess would ask for a link nobody sent.
 *
 * Pure on purpose: no Angular.
 */

export interface AddressStatus {
  /** True while the person has an email to open. */
  asked: boolean;
  confirmed: boolean;
  email: string;
  /** 0 when « Renvoyer le lien » may be pressed now. */
  resendInSeconds: number;
}

/** The reason of the 403 the API answers on the three actions that wait. */
export const EMAIL_NOT_CONFIRMED = 'email_not_confirmed';

export const isAskedToConfirm = (status: AddressStatus | undefined | null): boolean =>
  status?.asked === true && status.confirmed !== true;

/** Whether an HTTP error is the API saying the address is not confirmed yet. */
export const isRefusedForTheAddress = (error: unknown): boolean => {
  const e = error as { status?: number; error?: { reason?: unknown } } | undefined;
  return e?.status === 403 && e.error?.reason === EMAIL_NOT_CONFIRMED;
};

/** « 0:48 », as the button says it. */
export const countdown = (seconds: number): string => {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * The domains people mistype most, and what they meant. Short on purpose: a
 * hint that fires on a real address (« tu voulais dire… » under somebody's
 * own company domain) is worse than no hint.
 */
const MEANT: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.fr': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmai.fr': 'hotmail.fr',
  'hotmial.fr': 'hotmail.fr',
  'yaho.fr': 'yahoo.fr',
  'yahou.fr': 'yahoo.fr',
  'yahoo.co': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlok.fr': 'outlook.fr',
  'orange.f': 'orange.fr',
  'oragne.fr': 'orange.fr',
  'icloud.co': 'icloud.com',
  'iclou.com': 'icloud.com',
  'wanadoo.f': 'wanadoo.fr',
  'free.f': 'free.fr',
  'laposte.ne': 'laposte.net',
};

/** « gmail.com » when the address ends in a known typo of it, else undefined. */
export const theDomainMeant = (email: string): string | undefined => {
  const at = email.trim().lastIndexOf('@');
  if (at < 1) {
    return undefined;
  }
  return MEANT[
    email
      .trim()
      .slice(at + 1)
      .toLowerCase()
  ];
};

/** The address with the domain it meant. */
export const withTheDomainMeant = (email: string): string => {
  const meant = theDomainMeant(email);
  if (!meant) {
    return email;
  }
  const trimmed = email.trim();
  return `${trimmed.slice(0, trimmed.lastIndexOf('@') + 1)}${meant}`;
};

/** Close enough to an address to be sent: the API reads it again. */
export const looksLikeAnAddress = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

/**
 * The banner can be closed, and comes back at most once a day (mockup voted on
 * 28 October 2026): closed today, it stays closed until tomorrow.
 */
export const BANNER_CLOSED_KEY = 'addressBannerClosedOn';

export const aDay = (date: Date): string => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

export const theBannerShows = (status: AddressStatus | undefined | null, closedOn: string | null, now: Date): boolean =>
  isAskedToConfirm(status) && closedOn !== aDay(now);

/**
 * A referral code the API refused because the address was not confirmed yet.
 * Kept on the device and claimed again once the address is, so that an
 * invitee who confirms the next day still credits whoever invited them.
 */
export const REFERRAL_CODE_WAITING_KEY = 'referralCodeWaitingForTheAddress';

/**
 * The message key for what « Renvoyer le lien » was refused, one per reason the
 * API gives. Anything else (no network, a 500) gets the generic one: the
 * person can press again once the countdown, if any, is over.
 */
export const whySendingWasRefused = (error: unknown): string => {
  const e = error as { status?: number; error?: { reason?: unknown } } | undefined;
  switch (e?.error?.reason) {
    case 'resend_too_soon':
      return 'EMAIL_CONFIRMATION.ERRORS.TOO_SOON';
    case 'email_taken':
      return 'EMAIL_CONFIRMATION.ERRORS.EMAIL_TAKEN';
    case 'invalid_email':
      return 'EMAIL_CONFIRMATION.ERRORS.INVALID_EMAIL';
    case 'address_of_a_provider':
      return 'EMAIL_CONFIRMATION.ERRORS.ADDRESS_OF_A_PROVIDER';
    default:
      return 'EMAIL_CONFIRMATION.ERRORS.NOT_SENT';
  }
};

/** The seconds a 429 says to wait, or undefined when the answer carries none. */
export const secondsToWait = (error: unknown): number | undefined => {
  const seconds = (error as { error?: { resendInSeconds?: unknown } } | undefined)?.error?.resendInSeconds;
  return typeof seconds === 'number' && seconds > 0 ? seconds : undefined;
};
